// React et hooks
import { useEffect, useMemo, useState, useContext, useRef, useLayoutEffect } from "react";

// MUI
import { Box, Paper, Button, Typography, useTheme, Skeleton, Stack, IconButton } from "@mui/material";
import { DataGridPro, GridColDef, GridToolbar, GridToolbarContainer, GridSortModel, useGridApiRef } from '@mui/x-data-grid-pro';

// Redux
import { useDispatch, useSelector } from "react-redux";

// Context et reducers
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { setFilterCompare, setFilterDate, setFilterDateCompare } from 'src/context/reducers/filter-settings/filterSlice';

// API queries
import { useGetCompetitiveLandscapeKpisQuery, useGetCompetitiveIntelligenceKpisQuery } from "src/context/api/competitorsMonitoring/api";

// Localisation
import { useLocales } from "src/locales";

// Utilitaires
import { fDate } from "src/utils/format-time";
import countriesJson from "src/utils/countries.json";
import { SplashScreen } from 'src/components/loading-screen';
import { useLocation } from 'react-router-dom';
import { subDays } from "date-fns";
import { Helmet } from 'react-helmet-async';
import {
  getLevelColor,
  mapLevelToLabel,
} from "src/components/competitive-intelligence/utils";
import { useCountryData } from "src/components/competitive-intelligence/hooks/useCountryData";

import { PresenceSegments } from "src/components/competitive-intelligence/types";
import { createDataGridColumns } from "src/components/competitive-intelligence/utils/datagrid-config";

// Composants
import PresenceRateChart from 'src/components/competitive-intelligence/components/PresenceRateChart';
import {CardBar} from 'src/components/competitive-intelligence/components/CardBar'; 
import { SearchTermAnalysisHeader } from "src/components/competitive-intelligence/components/SearchTermAnalysisHeader";
import type { PressurePoolDatum } from 'src/components/competitive-intelligence/components/ByPressureCard';
import type { TopCompetitorStat } from 'src/components/competitive-intelligence/components/TopCompetitorsCard';

// Hooks personnalisés
import { useResponsiveConfig } from "src/components/competitive-intelligence/utils/use-responsive-config";

// Context centralisé
import { CompetitiveIntelligenceContext } from "src/components/competitive-intelligence/components/CompetitiveIntelligenceProvider";

// Styles
import "src/components/competitive-intelligence/style.css";
import { CountryFilter } from "src/components/competitive-intelligence/components/CountryFilter";


export default function SearchTermsAnalysis() {
    const { t } = useLocales();
    const dispatch = useDispatch();
    const theme = useTheme();
    const location = useLocation();
    const prefilledFromQueryRef = useRef<boolean>(false);
    
    // Configuration responsive granulaire
    const responsiveConfig = useResponsiveConfig();

    // Essayer d'utiliser le contexte centralisé si disponible
    const context = useContext(CompetitiveIntelligenceContext);

    // Fallback pour l'utilisation standalone
    const standaloneWorkspace: any = useCurrentWorkspace();
    const standaloneFilterStore = useSelector((state: any) => state.filters);

    // Utiliser le contexte s'il est disponible, sinon les valeurs standalone
    const workspace = context?.workspace || standaloneWorkspace;
    const filterStore = context?.filterStore || standaloneFilterStore;
    const isLoadingBaseData = context?.isLoadingBaseData || false;

    // Fetch competitive intelligence data uniquement si pas de contexte ET si on est bien dans le mode standalone
    // Si le contexte existe, la query a déjà été exécutée par le Provider
    const skipBaseQuery = context ? true : (!filterStore.start || !filterStore.end || !workspace?.id);

    const { data: standaloneCompetitiveIntelligenceData } = useGetCompetitiveIntelligenceKpisQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        timestep: filterStore.timestep,
        devices: filterStore.devices,
        countries: filterStore.countries,
        keywords: filterStore.keywords,
        platforms: filterStore?.platforms,
        workspaceId: workspace?.id,
    }, {
        skip: skipBaseQuery,
    });

    // Utiliser les données du contexte ou les données standalone
    const competitiveIntelligenceData = context?.competitiveIntelligenceData ||
        (Array.isArray(standaloneCompetitiveIntelligenceData) ? standaloneCompetitiveIntelligenceData : []);

    // Traitement des pays (contexte ou standalone)
    // Fetch countries data
    const { countriesData } = useCountryData();

    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [isAbsMode, setIsAbsMode] = useState<boolean>(false);

    // Normalize adType for API:
    const isShoppingEnabled = workspace?.template === 'E-commerce' || workspace?.shoppingIncluded === true;
    const normalizedAdType = (Array.isArray(filterStore?.adType) && filterStore.adType.length === 0)
        ? (isShoppingEnabled ? ['TXT', 'PLA'] : ['TXT'])
        : filterStore?.adType;

    // Cette query doit toujours s'exécuter car elle fournit les données du tableau
    // Elle ne devrait pas être skippée même si le contexte est présent
    // On skip seulement si les filtres de base ne sont pas disponibles
    const skipStaQuery = !filterStore.start || !filterStore.end || !workspace?.id || (!selectedCountry && (!Array.isArray(filterStore.countries) || filterStore.countries.length === 0));

    const { data: competitiveLandscapeData, error: competitiveLandscapeError, isFetching: isFetchingCompetitiveLandscape } = useGetCompetitiveLandscapeKpisQuery({
        startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
        endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
        compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, "yyyy-MM-dd") : null,
        compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, "yyyy-MM-dd") : null,
        timestep: filterStore.timestep,
        devices: filterStore.devices,
        // Send country NAME directly when a country is selected (no Redux involved)
        countries: selectedCountry ? [selectedCountry] : filterStore.countries,
        keywords: filterStore.keywords,
        adType: normalizedAdType,
        workspaceId: workspace?.id,
    }, {
        skip: skipStaQuery,
    });

    // Les filtres sont déjà initialisés dans le Redux store, pas besoin de les réinitialiser

    // Initialisation pays: contexte > query param > premier pays (fallback standalone)
    useEffect(() => {
        // 1) Contexte (le plus prioritaire si présent)
        if (context?.selectedCountry != null) {
            setSelectedCountry(context.selectedCountry);
            return;
        }
        // 2) Deep link via ?country=FR (code ISO → nom)
        const params = new URLSearchParams(location.search || (typeof window !== 'undefined' ? window.location.search : ''));
        const codeParam = params.get('country') || params.get('c');
        if (codeParam && countriesData.length > 0) {
            const mapped = countriesJson?.find((c) => String(c.code).toUpperCase() === String(codeParam).toUpperCase())?.name;
            if (mapped && countriesData.some((c) => c.country_name === mapped)) {
                prefilledFromQueryRef.current = true;
                setSelectedCountry(mapped);
                return;
            }
        }
        // 3) Fallback: sélectionner le premier pays disponible
        if (!prefilledFromQueryRef.current && !selectedCountry && countriesData.length > 0) {
            setSelectedCountry(countriesData[0].country_name);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context?.selectedCountry, location.search, countriesData]);

    // Fonction helper pour obtenir la couleur selon le niveau avec le thème actuel
    const getThemeColor = (level?: string) => getLevelColor(level, theme);

    // Controlled sorting with error handling for rapid page switching
    const apiRef = useGridApiRef();
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'searchTerm', sort: 'desc' }]);
    const [hasError, setHasError] = useState(false);

    // Cleanup apiRef on component unmount to prevent ARIA errors during rapid switching
    // This addresses the useGridRowAriaAttributes error when switching between overview and search-term-analysis
    useEffect(() => {
        return () => {
            // Reset sort model to clean up DataGrid state before unmounting
            if (apiRef.current) {
                try {
                    apiRef.current.setSortModel([]);
                } catch (error) {
                    // Silently ignore cleanup errors to prevent console spam
                }
            }
        };
    }, [apiRef]);

    const toggleTrafficShareSort = () => {
        try {
            // Get current sort state and toggle direction
            const currentModel = (apiRef.current?.getSortModel?.() as GridSortModel | undefined) || sortModel;
            const current = currentModel[0];
            const currentDir = current && current.field === 'searchTerm' ? current.sort : sortModel[0]?.sort;
            const nextDir = currentDir === 'desc' ? 'asc' : 'desc';
            const nextModel: GridSortModel = [{ field: 'searchTerm', sort: nextDir }];
            
            // Apply new sort model to both apiRef and local state
            apiRef.current?.setSortModel?.(nextModel);
            setSortModel(nextModel);
            setHasError(false);
        } catch (error) {
            // Handle errors gracefully during rapid switching
            console.warn('Error in toggleTrafficShareSort:', error);
            setHasError(true);
        }
    };

    // Create columns for the DataGrid using externalized configuration
    const columns: GridColDef[] = createDataGridColumns({
        responsiveConfig,
        theme,
        t,
        workspace,
        sortModel,
        toggleTrafficShareSort,
        PresenceRateChart,
        columnType: 'searchTerm'
    });

    const rows = (competitiveLandscapeData as any[] | undefined)?.map((item: any, index: number) => {
        // Filter competitors with at least 1% presence rate, but always include the first one even at 0%
        const allCompetitors = Array.isArray(item?.top_5_competitors) ? item.top_5_competitors : [];
        const firstCompetitor = allCompetitors[0];
        const otherCompetitors = allCompetitors.slice(1).filter((c: any) => {
            const rate = c?.paid_presence_rate;
            return typeof rate === 'number' && rate >= 1;
        });
        const filteredCompetitors = firstCompetitor ? [firstCompetitor, ...otherCompetitors] : otherCompetitors;
        
        const topCompetitors = filteredCompetitors.map((c: any) => c?.domain_name).filter(Boolean);
        const topCompetitorsPaidRates: Record<string, number> = filteredCompetitors.reduce((acc: Record<string, number>, c: any) => {
            const domain = c?.domain_name;
            const rate = c?.paid_presence_rate;
            if (domain && typeof rate === 'number') acc[domain] = rate;
            return acc;
        }, {});

        const firstCompetitorDomain = filteredCompetitors.length > 0 && filteredCompetitors[0]?.domain_name
            ? filteredCompetitors[0].domain_name
            : undefined;

        const isNotScrapped = item?.status === 'not scrapped';

        return {
            id: `${item?.country_name ?? 'NA'}__${item?.search_term ?? index}`,
            searchTerm: item?.search_term ?? '',
            country: item?.country_name ?? '',
            trafficShare: item?.traffic_share ?? 0,
            competitiveLandscape: isNotScrapped ? null : mapLevelToLabel(isAbsMode ? item?.abs_level : item?.level),
            levelRaw: isNotScrapped ? null : (isAbsMode ? item?.abs_level : item?.level),
            brandDomain: workspace?.url,
            firstCompetitorDomain: isNotScrapped ? undefined : firstCompetitorDomain,
            isDemoBrand: workspace?.name?.includes('Demo'),
            topCompetitors: isNotScrapped ? [] : topCompetitors,
            topCompetitorsPaidRates: isNotScrapped ? {} : topCompetitorsPaidRates,
            // Brand paid presence rate (client)
            // Prefer per-mode fields from search_term_analysis.sql; fallback to overview field name if present
            paidPresenceRate: isNotScrapped ? null : (isAbsMode
                ? (item?.client_abs_top_paid_presence_rate ?? item?.client_paid_presence_rate ?? 0)
                : (item?.client_top_paid_presence_rate ?? item?.client_paid_presence_rate ?? 0)),
            blendedPresenceRate: isNotScrapped ? null : (isAbsMode ? (item?.abs_top_blended_presence_rate ?? 0) : (item?.blended_presence_rate ?? 0)),
            // Paid presence rates for tooltip
            firstCompetitorPaidPresenceRate: isNotScrapped ? null : (firstCompetitorDomain ? (item?.top_5_competitors?.find((c: any) => c?.domain_name === firstCompetitorDomain)?.paid_presence_rate ?? 0) : 0),
            presenceRateData: isNotScrapped ? { organicOnly: 0, soloBidder: 0, topAdPosition: 0 } as PresenceSegments : ({
                organicOnly: isAbsMode ? (item?.abs_top_organic_only ?? 0) : (item?.organic_only ?? 0),
                soloBidder: isAbsMode ? (item?.abs_top_solo_bidder ?? 0) : (item?.solo_bidder ?? 0),
                topAdPosition: isAbsMode ? (item?.abs_top_ad_position ?? 0) : (item?.top_ad_position ?? 0),
            } as PresenceSegments),
            status: item?.status,
        };
    }) ?? [];

    // Calculate pressure pools by search term (similar to overview but for keywords)
    const pressurePools = useMemo<PressurePoolDatum[]>(() => {
        type PressureLevel = 'high' | 'active' | 'low';
        const levelOrder: PressureLevel[] = ['high', 'active', 'low'];
        const counts: Record<PressureLevel, number> = {
            high: 0,
            active: 0,
            low: 0,
        };
        const searchTermsByLevel: Record<PressureLevel, Array<{ name: string; trafficShare: number }>> = {
            high: [],
            active: [],
            low: [],
        };

        rows.forEach((row) => {
            const key = typeof row.levelRaw === 'string' ? row.levelRaw.toLowerCase() : '';
            if (key === 'high' || key === 'active' || key === 'low') {
                counts[key as PressureLevel] += 1;
                const searchTerm = row.searchTerm;
                const trafficShare = typeof row.trafficShare === 'number' ? row.trafficShare : 0;
                if (searchTerm && typeof searchTerm === 'string') {
                    searchTermsByLevel[key as PressureLevel].push({
                        name: searchTerm,
                        trafficShare: trafficShare,
                    });
                }
            }
        });

        const totalTracked = levelOrder.reduce((sum, level) => sum + counts[level], 0);
        const toPercentage = (count: number) => (totalTracked > 0 ? Math.round((count / totalTracked) * 100) : 0);

        return levelOrder.map((level) => ({
            level,
            label: mapLevelToLabel(level),
            countryCount: counts[level],
            percentage: toPercentage(counts[level]),
            countries: searchTermsByLevel[level].sort((a, b) => b.trafficShare - a.trafficShare),
        }));
    }, [rows]);

    // Calculate top competitors data from competitive landscape data
    const topCompetitorsData = useMemo<TopCompetitorStat[]>(() => {
        const stats = new Map<string, { total: number; count: number }>();
        
        // Get workspace domains to exclude them from competitors
        const workspaceDomains = new Set<string>();
        if (workspace?.url) {
            workspaceDomains.add(workspace.url.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, ''));
        }
        if (workspace?.friendlyDomains && Array.isArray(workspace.friendlyDomains)) {
            workspace.friendlyDomains.forEach((d: string) => {
                const normalized = d.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
                workspaceDomains.add(normalized);
            });
        }

        (competitiveLandscapeData as any[] | undefined)?.forEach((item: any) => {
            if (!Array.isArray(item?.top_5_competitors)) return;
            item.top_5_competitors.forEach((competitor: any) => {
                const domain = competitor?.domain_name;
                const rate = competitor?.paid_presence_rate;
                if (!domain || typeof rate !== 'number' || rate <= 0) return;
                
                // Normalize domain for comparison
                const normalizedDomain = domain.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
                if (workspaceDomains.has(normalizedDomain)) return; // Skip workspace domains
                
                const current = stats.get(domain) || { total: 0, count: 0 };
                current.total += rate;
                current.count += 1;
                stats.set(domain, current);
            });
        });

        return Array.from(stats.entries())
            .map(([domain, { total, count }]) => ({
                domain: domain.trim(),
                paidPresenceRate: count ? Math.round((total / count) * 10) / 10 : 0,
            }))
            .filter(item => item.paidPresenceRate > 0)
            .sort((a, b) => b.paidPresenceRate - a.paidPresenceRate)
            .slice(0, 3);
    }, [competitiveLandscapeData, workspace]);

    // Extract weekly series data (from first item if available)
    const blendedPresenceRateWeeklySeries = useMemo(() => {
        const firstItem = (competitiveLandscapeData as any[] | undefined)?.[0];
        if (Array.isArray(firstItem?.blended_presence_rate_weekly_series)) {
            return firstItem.blended_presence_rate_weekly_series;
        }
        return undefined;
    }, [competitiveLandscapeData]);

    // Calculate top metric (blended presence rate) as average over all search terms
    const blendedPresenceTopMetric = useMemo(() => {
        const validRates = rows
            .map((row) => row.blendedPresenceRate)
            .filter((rate): rate is number => typeof rate === 'number' && rate > Math.round(0));
        if (validRates.length > 0) {
            const averageRate = validRates.reduce((sum: number, rate: number) => sum + rate, 0) / validRates.length;
            return `${Math.round(averageRate)}%`;
        }
        return undefined;
    }, [rows]);

    if (!filterStore?.countries || !filterStore?.keywords || !filterStore?.devices) {
        return <SplashScreen />;
    }

    // Custom toolbar rendered within DataGrid context
    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ p: 0, minHeight: 30, backgroundColor: theme.palette.mode === 'dark' ? '#383F48' : '#f4f6f8'}}>
                <Box sx={{ px: 1, py: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1, backgroundColor: theme.palette.mode === 'dark' ? '#383F48' : '#f4f6f8', flexWrap: 'nowrap', borderBottom: '1px dashed rgba(145, 158, 171, 0.2)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', minWidth: 0, overflow: 'hidden' }}>
                        <GridToolbar sx={{ flexWrap: 'nowrap', gap: 0.5, '& .MuiButton-root': { whiteSpace: 'nowrap' } }} />
                    </Box>
                    <Box sx={{ flexShrink: 0 }}>
                        <SearchTermAnalysisHeader isAbsMode={isAbsMode} onToggleAbsMode={() => setIsAbsMode((v) => !v)} />
                    </Box>
                </Box>
            </GridToolbarContainer>
        );
    }

    useLayoutEffect(() => {
        const now = new Date();
        const startDate = new Date(now);
        startDate.setDate(now.getDate() - 30);

        // Initialise start et end pour éviter skip des requêtes au premier rendu
        dispatch(setFilterDate({
            start: startDate.toISOString(),
            end: now.toISOString(),
        }));

        // Désactiver la comparaison par défaut et initialiser les dates de comparaison
        dispatch(setFilterCompare(false));
        dispatch(setFilterDateCompare({
            compareStart: subDays(now, 60).toISOString(),
            compareEnd: subDays(now, 31).toISOString(),
        }));
    }, [])

    return (
        <Box>
        <Box sx={{marginBottom: '10px' }}>
          <CountryFilter
                    countries={countriesData}
                    selectedCountry={selectedCountry}
                    onCountryChange={(c) => {
                      setSelectedCountry(c);
                      context?.setSelectedCountry(c);
                    }}
                    backgroundColor="transparent"
                    sx={{maginBottom:"13px"}}
                />
        </Box>
          <Helmet>
            <title>Search Term Analysis</title>
          </Helmet>
        <CardBar
              blendedPresenceTitle="Blended Presence Rate"
              blendedPresenceTopMetric={blendedPresenceTopMetric}
              blendedPresenceWeeklySeries={blendedPresenceRateWeeklySeries}
              pressureTitle="Search Terms By Pressure"
              topCompetitorsTitle="Top Competitors"
              pressurePools={pressurePools}
              topCompetitorsData={topCompetitorsData}
              loading={isFetchingCompetitiveLandscape}
              emptyMessage="No search term found for this pool"
            />
            <Paper sx={{ 
                p: 0, 
                overflow: 'hidden',
                // Enhanced border visibility for light mode - englobes both filter and DataGrid - lighter
                border: `1px solid ${theme.palette.mode === 'dark' ? '#919eab33' : '#f0f0f0'}`,
                borderRadius: 1,
            }}>

                <Box
                    sx={{
                        zIndex: -100,
                        width: '100%',
                        // No border needed here - parent Paper already has the border
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                            outline: 'none !important',
                        },
                        '& .MuiDataGrid-cell': {
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            // Override des bordures dashed par des bordures solides - lighter
                            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#919eab33' : '#f0f0f0'} !important`,
                        },
                        '& .row-not-scrapped .MuiDataGrid-cell': {
                            color: theme.palette.text.disabled,
                        },
                        '& .leftAlignCell': {
                            justifyContent: 'flex-start !important',
                        },
                        '& .leftAlignCell .MuiDataGrid-cellContent': {
                            justifyContent: 'flex-start !important',
                            textAlign: 'left',
                            width: 'auto',
                            marginRight: 'auto',
                        },
                        '& .MuiDataGrid-row': {
                            alignItems: 'center',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            display: 'flex',
                            alignItems: 'center',
                            // Enhanced header border visibility - lighter
                            borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#919eab33' : '#f0f0f0'} !important`,
                        },
                        '& .MuiDataGrid-columnHeader--alignLeft .MuiDataGrid-columnHeaderTitleContainer': {
                            justifyContent: 'flex-start',
                        },
                        '& .MuiDataGrid-cellContent': {
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                        },
                        '& .MuiDataGrid-cell[data-field="competitiveLandscape"]': {
                            justifyContent: 'flex-start !important',
                            textAlign: 'left',
                        },
                        '& .MuiDataGrid-cell[data-field="competitiveLandscape"] > .MuiDataGrid-cellContent': {
                            justifyContent: 'flex-start !important',
                            textAlign: 'left !important',
                            width: 'auto',
                            marginRight: 'auto',
                        },
                        '& .MuiDataGrid-cell.MuiDataGrid-cell--textLeft[data-field="competitiveLandscape"]': {
                            justifyContent: 'flex-start !important',
                        },
                        // Masquer la flèche de tri native sur la colonne searchTerm
                        '& .MuiDataGrid-columnHeader[data-field="searchTerm"] .MuiDataGrid-sortIcon': {
                            display: 'none !important',
                        },
                        // Ensure header title container spans full width to right-align custom (icon + arrow)
                        '& .MuiDataGrid-columnHeader.searchTermHeader .MuiDataGrid-columnHeaderTitleContainer': {
                            width: '100%',
                            flex: '1 1 auto',
                            overflow: 'visible',
                            paddingRight: 0,
                            minWidth: 0,
                            marginRight: '0 !important',
                            display: 'flex !important',
                            alignItems: 'center',
                            justifyContent: 'space-between !important',
                        },
                        '& .MuiDataGrid-columnHeader.searchTermHeader .MuiDataGrid-columnHeaderTitleContainerContent': {
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        },
                        '& .MuiDataGrid-columnHeader.searchTermHeader .MuiDataGrid-columnHeaderTitle': {
                            display: 'flex',
                            alignItems: 'center',
                            flex: '1 1 auto',
                            minWidth: 0,
                            marginRight: '8px',
                        },
                        '& .MuiDataGrid-columnHeader.searchTermHeader .MuiDataGrid-columnHeaderDraggableContainer': {
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                        },
                        '& .MuiDataGrid-columnHeader.searchTermHeader': {
                            paddingRight: '8px',
                        },
                        '& .MuiDataGrid-columnHeader.searchTermHeader .MuiDataGrid-menuIconButton': {
                            display: 'none !important',
                        },
                        '& .MuiDataGrid-columnHeader.searchTermHeader .MuiDataGrid-iconSeparator': {
                            display: 'none !important',
                        },
                        // Suppression de la bordure du toolbar container
                        '& .MuiDataGrid-toolbarContainer': {
                            borderBottom: 'none !important',
                        },
                    }}
                >
                    {isFetchingCompetitiveLandscape ? <Skeleton variant="rectangular" height={500} /> : hasError ? (
                        // Error fallback UI for DataGrid rendering issues during rapid switching
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="h6" color="error" gutterBottom>
                                {t('dashboard.competitiveIntelligence.dataGridError.title')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t('dashboard.competitiveIntelligence.dataGridError.message')}
                            </Typography>
                            <Button 
                                variant="outlined" 
                                onClick={() => setHasError(false)}
                            >
                                {t('dashboard.competitiveIntelligence.dataGridError.retry')}
                            </Button>
                        </Box>
                    ) : (
                        <DataGridPro
                            // Unique key forces complete remount on workspace/country change, preventing ARIA conflicts
                            key={`search-term-datagrid-${workspace?.id || 'default'}-${selectedCountry || 'all'}`}
                            apiRef={apiRef}
                            autoHeight
                            disableDensitySelector
                            checkboxSelection={false}
                            disableRowSelectionOnClick
                            showCellVerticalBorder
                            rows={rows}
                            columns={columns}
                            getRowClassName={(params) => (params.row?.status === 'not scrapped' ? 'row-not-scrapped' : '')}
                            sortingMode="client"
                            disableMultipleColumnsSorting
                            sortingOrder={['desc', 'asc']}
                            sortModel={sortModel}
                            onSortModelChange={(newModel) => {
                                try {
                                    // Update local sort state, handled by DataGrid internally
                                    setSortModel(newModel);
                                } catch (error) {
                                    // Handle sort model errors during rapid switching
                                    console.warn('Error in onSortModelChange:', error);
                                }
                            }}
                            pagination
                            pageSizeOptions={[10, 20, 50, 100]}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 10, page: 0 },
                                },
                            }}
                            slots={{ toolbar: CustomToolbar }}
                            slotProps={{
                                toolbar: {
                                    printOptions: { disableToolbarButton: true },
                                    csvOptions: { disableToolbarButton: false }
                                },
                            }}
                        />
                    )}
                </Box>
            </Paper>
        </Box>
    );
}
